import {
	ChevronRightIcon,
	EyeIcon,
	FileTextIcon,
	GlobeIcon,
	RocketIcon,
} from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { z } from "zod";
import { formatClonePlan } from "./clone-status.js";
import { SiteFavicon, hostFromUrl } from "./SiteFavicon.js";

const siteRowSchema = z.object({
	siteId: z.string(),
	name: z.string(),
	sourceUrl: z.string(),
	role: z.enum(["primary", "landing", "other"]).optional().default("primary"),
	status: z.string(),
	phase: z.string().optional().default("pending"),
	progress: z.coerce.number().optional().default(0),
	plannedPageCount: z.number().int().nullable().optional(),
	pagesCompleted: z.number().int().nullable().optional(),
	etaRemainingSec: z.number().int().nullable().optional(),
	previewUrl: z.string().nullable().optional(),
	publishedUrl: z.string().nullable().optional(),
	lastError: z.string().nullable().optional(),
});

type HostedSite = z.infer<typeof siteRowSchema>;
export type SiteTone = "ready" | "work" | "fail";

async function fetchHostedSites(): Promise<HostedSite[]> {
	try {
		const response = await fetch("/api/sites", { credentials: "include" });
		if (!response.ok) return [];
		const json: unknown = await response.json();
		if (!json || typeof json !== "object") return [];
		const raw = (json as { sites?: unknown }).sites;
		if (!Array.isArray(raw)) return [];
		return raw.flatMap((row) => {
			const parsed = siteRowSchema.safeParse(row);
			return parsed.success ? [parsed.data] : [];
		});
	} catch {
		return [];
	}
}

function siteTone(status: string): SiteTone {
	switch (status) {
		case "preview_ready":
		case "published":
			return "ready";
		case "failed":
			return "fail";
		default:
			return "work";
	}
}

function isPublished(site: HostedSite): boolean {
	return site.status === "published" || Boolean(site.publishedUrl?.trim());
}

function siteStatusLabel(site: HostedSite): string {
	if (isPublished(site)) return "Published";
	if (site.status === "publishing") return "Publishing";
	const tone = siteTone(site.status);
	switch (tone) {
		case "ready":
			return "";
		case "fail":
			return site.lastError?.trim() || "Couldn’t finish";
		case "work": {
			const plan = formatClonePlan(site);
			return plan ?? "Still building";
		}
		default: {
			const _exhaustive: never = tone;
			return _exhaustive;
		}
	}
}

function isPublic(site: HostedSite): boolean {
	return isPublished(site);
}

function siteHost(site: HostedSite): string {
	return hostFromUrl(site.sourceUrl || site.name) || site.name;
}

function siteRoleLabel(site: HostedSite): string {
	switch (site.role) {
		case "landing":
			return "Landing page";
		case "other":
			return "Other site";
		case "primary":
			return "Main website";
		default: {
			const _exhaustive: never = site.role;
			return _exhaustive;
		}
	}
}

/** Always the hosted copy — never the public domain (those often refuse iframes). */
function siteOpenUrl(site: HostedSite): string | null {
	const stored = site.previewUrl?.trim() ?? "";
	if (
		stored.startsWith("/api/sites/") &&
		stored.includes("/preview")
	) {
		return stored;
	}
	if (siteTone(site.status) === "ready") {
		return `/api/sites/${site.siteId}/preview/index.html`;
	}
	return stored || null;
}

type Props = {
	onOpenPreview?: (url: string) => void;
	onPublish?: (siteId: string, siteName: string) => void;
};

/** Projects first — open one for preview, domain, and forms. */
export function SitesHub({ onOpenPreview, onPublish }: Props) {
	const [sites, setSites] = useState<HostedSite[]>([]);
	const [loaded, setLoaded] = useState(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);

	useEffect(() => {
		let cancelled = false;
		const load = async () => {
			const rows = await fetchHostedSites();
			if (!cancelled) {
				setSites(rows);
				setLoaded(true);
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
	}, []);

	const watchingBuild = sites.some(
		(site) =>
			site.status === "cloning" ||
			site.status === "pending" ||
			site.status === "publishing",
	);
	useEffect(() => {
		if (!watchingBuild) return;
		const timer = window.setInterval(() => {
			void fetchHostedSites().then(setSites);
		}, 3000);
		return () => {
			window.clearInterval(timer);
		};
	}, [watchingBuild]);

	const selected = sites.find((site) => site.siteId === selectedId) ?? null;

	if (selected) {
		return (
			<SiteProject
				site={selected}
				onBack={() => setSelectedId(null)}
				onOpenPreview={onOpenPreview}
				onPublish={onPublish}
			/>
		);
	}

	return (
		<>
			<header className="main-header agents-hub-header">
				<div className="min-w-0">
					<h1 className="main-title">Websites</h1>
				</div>
			</header>
			<div className="main sites-hub">
				{!loaded ? (
					<p className="sites-hub-empty">Loading…</p>
				) : sites.length === 0 ? (
					<p className="sites-hub-empty">
						No websites yet. Ask Mairko to start one.
					</p>
				) : (
					<ul className="sites-project-list">
						{sites.map((site) => {
							const tone = siteTone(site.status);
							const status = siteStatusLabel(site);
							return (
								<li key={site.siteId}>
									<button
										type="button"
										className="sites-project"
										onClick={() => setSelectedId(site.siteId)}
									>
										<SiteFavicon
											url={site.sourceUrl || site.name}
											live={tone === "work"}
										/>
										<div className="min-w-0 flex-1 text-left">
											<p className="sites-hub-name">
												{siteHost(site)}
											</p>
											<p className="sites-hub-meta">
												{siteRoleLabel(site)}
											</p>
										</div>
										{status ? (
											<span className={siteBadgeClass(tone)}>
												{status}
											</span>
										) : null}
										<ChevronRightIcon
											className="size-4 shrink-0 text-mx-quaternary"
											aria-hidden
										/>
									</button>
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</>
	);
}

type ProjectPane = "home" | "domain" | "forms";

function siteBadgeClass(tone: SiteTone): string {
	switch (tone) {
		case "ready":
			return "integration-badge integration-badge--ok";
		case "fail":
			return "integration-badge integration-badge--warn";
		case "work":
			return "integration-badge";
		default: {
			const _exhaustive: never = tone;
			return _exhaustive;
		}
	}
}

function SiteProject({
	site,
	onBack,
	onOpenPreview,
	onPublish,
}: {
	site: HostedSite;
	onBack: () => void;
	onOpenPreview?: (url: string) => void;
	onPublish?: (siteId: string, siteName: string) => void;
}) {
	const [pane, setPane] = useState<ProjectPane>("home");
	const host = siteHost(site);
	const openUrl = siteOpenUrl(site);
	const tone = siteTone(site.status);
	const brandUrl = site.sourceUrl || site.name;

	const openPreview = () => {
		if (openUrl && onOpenPreview) onOpenPreview(openUrl);
	};

	if (pane === "domain") {
		return (
			<div className="detail">
				<button
					type="button"
					className="detail-back"
					onClick={() => setPane("home")}
				>
					← {host}
				</button>
				<div className="detail-layout">
					<div className="detail-main">
						<WebsiteAddressPanel
							host={host}
							published={isPublic(site)}
							publishedUrl={site.publishedUrl}
							tone={tone}
							publishing={site.status === "publishing"}
							onPublish={
								onPublish
									? () => onPublish(site.siteId, site.name)
									: undefined
							}
						/>
					</div>
				</div>
			</div>
		);
	}

	if (pane === "forms") {
		return (
			<div className="detail">
				<button
					type="button"
					className="detail-back"
					onClick={() => setPane("home")}
				>
					← {host}
				</button>
				<div className="detail-layout">
					<div className="detail-main">
						<WebsiteMessagesPanel />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="detail">
			<button type="button" className="detail-back" onClick={onBack}>
				← Websites
			</button>
			<div className="detail-layout">
				<div className="detail-main">
					<header
						className={
							tone === "ready"
								? "detail-hero detail-hero--connected"
								: tone === "fail"
									? "detail-hero detail-hero--attention"
									: "detail-hero"
						}
					>
						<span className="detail-hero-mark sites-hero-mark" aria-hidden>
							<SiteFavicon url={brandUrl} />
						</span>
						<div className="detail-hero-body">
							<div className="detail-title-row">
								<h1 className="detail-name">{host}</h1>
								{siteStatusLabel(site) ? (
									<span className={siteBadgeClass(tone)}>
										{siteStatusLabel(site)}
									</span>
								) : null}
							</div>
							<p className="detail-blurb">
								{siteRoleLabel(site)}. Domain, forms, and the page itself.
							</p>
						</div>
					</header>

					<nav className="sites-tiles" aria-label="On this site">
						{openUrl && onOpenPreview ? (
							<button
								type="button"
								className="sites-tile"
								onClick={openPreview}
							>
								<span className="sites-tile-icon" aria-hidden>
									<EyeIcon className="size-4" />
								</span>
								<span className="min-w-0 flex-1 text-left">
									<span className="sites-hub-name block">
										Website
									</span>
									<span className="sites-hub-meta block">
										Open the preview
									</span>
								</span>
								<ChevronRightIcon
									className="size-4 shrink-0 text-mx-quaternary"
									aria-hidden
								/>
							</button>
						) : null}
						{onPublish &&
						site.status !== "publishing" &&
						site.status !== "failed" &&
						(site.status === "preview_ready" ||
							site.status === "published" ||
							Boolean(openUrl)) ? (
							<button
								type="button"
								className="sites-tile"
								onClick={() => onPublish(site.siteId, site.name)}
							>
								<span className="sites-tile-icon" aria-hidden>
									<RocketIcon className="size-4" />
								</span>
								<span className="min-w-0 flex-1 text-left">
									<span className="sites-hub-name block">
										{site.status === "published"
											? "Publish again"
											: "Publish"}
									</span>
									<span className="sites-hub-meta block">
										{site.status === "published"
											? "Ship the latest version"
											: "Go live on a Mairko address"}
									</span>
								</span>
								<ChevronRightIcon
									className="size-4 shrink-0 text-mx-quaternary"
									aria-hidden
								/>
							</button>
						) : null}
						{site.status === "publishing" ? (
							<div className="sites-tile" aria-live="polite">
								<span className="sites-tile-icon" aria-hidden>
									<RocketIcon className="size-4" />
								</span>
								<span className="min-w-0 flex-1 text-left">
									<span className="sites-hub-name block">
										Publishing
									</span>
									<span className="sites-hub-meta block">
										Building the live address
									</span>
								</span>
							</div>
						) : null}
						{site.publishedUrl ? (
							<a
								className="sites-tile"
								href={site.publishedUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								<span className="sites-tile-icon" aria-hidden>
									<RocketIcon className="size-4" />
								</span>
								<span className="min-w-0 flex-1 text-left">
									<span className="sites-hub-name block">
										Live site
									</span>
									<span className="sites-hub-meta block">
										{site.publishedUrl.replace(/^https?:\/\//, "")}
									</span>
								</span>
								<ChevronRightIcon
									className="size-4 shrink-0 text-mx-quaternary"
									aria-hidden
								/>
							</a>
						) : null}
						<button
							type="button"
							className="sites-tile"
							onClick={() => setPane("domain")}
						>
							<span className="sites-tile-icon" aria-hidden>
								<GlobeIcon className="size-4" />
							</span>
							<span className="min-w-0 flex-1 text-left">
								<span className="sites-hub-name block">
									Domain
								</span>
								<span className="sites-hub-meta block">
									{site.publishedUrl
										? "Live on Mairko · your domain next"
										: `${host} · only you can see this`}
								</span>
							</span>
							<ChevronRightIcon
								className="size-4 shrink-0 text-mx-quaternary"
								aria-hidden
							/>
						</button>
						<button
							type="button"
							className="sites-tile"
							onClick={() => setPane("forms")}
						>
							<span className="sites-tile-icon" aria-hidden>
								<FileTextIcon className="size-4" />
							</span>
							<span className="min-w-0 flex-1 text-left">
								<span className="sites-hub-name block">
									Forms
								</span>
								<span className="sites-hub-meta block">
									0 so far · from the website forms
								</span>
							</span>
							<ChevronRightIcon
								className="size-4 shrink-0 text-mx-quaternary"
								aria-hidden
							/>
						</button>
					</nav>
				</div>
			</div>
		</div>
	);
}

export function WebsiteAddressPanel({
	host,
	published,
	publishedUrl,
	tone,
	publishing = false,
	onPublish,
}: {
	host: string;
	published: boolean;
	publishedUrl?: string | null;
	tone: SiteTone;
	publishing?: boolean;
	onPublish?: () => void;
}) {
	const canPublish = Boolean(onPublish) && !published && !publishing;
	return (
		<>
			<header className="sites-pane-head">
				<h1 className="detail-name">Web address</h1>
				<p className="detail-blurb">
					{published
						? "Live on Mairko. Your own domain comes next."
						: "Publish to get a public Mairko address. Your domain comes after that."}
				</p>
			</header>
			<ul className="sites-hub-list">
				{publishedUrl ? (
					<li className="sites-hub-card">
						<RocketIcon
							className="size-4 shrink-0 text-mx-tertiary"
							aria-hidden
						/>
						<div className="min-w-0 flex-1">
							<p className="sites-hub-name">
								{publishedUrl.replace(/^https?:\/\//, "")}
							</p>
							<p className="sites-hub-meta">
								Anyone can open this.
							</p>
						</div>
						<span className={siteBadgeClass("ready")}>
							Published
						</span>
					</li>
				) : publishing ? (
					<li className="sites-hub-card">
						<RocketIcon
							className="size-4 shrink-0 text-mx-tertiary"
							aria-hidden
						/>
						<div className="min-w-0 flex-1">
							<p className="sites-hub-name">Publishing</p>
							<p className="sites-hub-meta">
								Building the public Mairko address
							</p>
						</div>
						<span className={siteBadgeClass("work")}>
							Publishing
						</span>
					</li>
				) : canPublish ? (
					<li>
						<button
							type="button"
							className="sites-tile w-full"
							onClick={onPublish}
						>
							<span className="sites-tile-icon" aria-hidden>
								<RocketIcon className="size-4" />
							</span>
							<span className="min-w-0 flex-1 text-left">
								<span className="sites-hub-name block">
									Publish
								</span>
								<span className="sites-hub-meta block">
									Go live on a Mairko address
								</span>
							</span>
							<ChevronRightIcon
								className="size-4 shrink-0 text-mx-quaternary"
								aria-hidden
							/>
						</button>
					</li>
				) : (
					<li className="sites-empty">
						<p className="sites-empty-title">Not public yet</p>
						<p className="sites-empty-lede">
							Ask Mairko to publish this site. Then we can
							point {host}.
						</p>
					</li>
				)}
				<li className="sites-hub-card">
					<GlobeIcon
						className="size-4 shrink-0 text-mx-tertiary"
						aria-hidden
					/>
					<div className="min-w-0 flex-1">
						<p className="sites-hub-name">{host}</p>
						<p className="sites-hub-meta">
							{published
								? "Your public name — we’ll point it here after DNS."
								: "Preview — only you can see this."}
						</p>
					</div>
					<span className={siteBadgeClass(tone)}>
						{published ? "Ready" : "Private"}
					</span>
				</li>
				{published ? (
					<li className="sites-empty">
						<p className="sites-empty-title">Your own address</p>
						<p className="sites-empty-lede">
							Want {host} to open this live site? Ask Mairko when
							you’re ready for DNS.
						</p>
					</li>
				) : null}
			</ul>
		</>
	);
}

export function WebsiteMessagesPanel() {
	return (
		<>
			<header className="sites-pane-head">
				<h1 className="detail-name">Messages</h1>
				<p className="detail-blurb">
					People who reach out from this website.
				</p>
			</header>
			<div className="sites-empty sites-empty--inbox">
				<FileTextIcon className="sites-empty-icon" aria-hidden />
				<p className="sites-empty-title">No messages yet</p>
				<p className="sites-empty-lede">
					When someone fills in a form on your website, it shows up
					here — you don’t have to hunt through email.
				</p>
			</div>
			<ul className="sites-hub-list">
				<li className="sites-hub-card">
					<div className="min-w-0 flex-1">
						<p className="sites-hub-name">Contact</p>
						<p className="sites-hub-meta">
							The “get in touch” form
						</p>
					</div>
					<span className="sites-hub-count">0</span>
				</li>
				<li className="sites-hub-card">
					<div className="min-w-0 flex-1">
						<p className="sites-hub-name">Ask a question</p>
						<p className="sites-hub-meta">
							The form people use to request info
						</p>
					</div>
					<span className="sites-hub-count">0</span>
				</li>
			</ul>
		</>
	);
}

const PREVIEW_SITE_ID_RE =
	/\/api\/sites\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\/preview/i;

const hostedStatusSchema = z.object({
	site: z
		.object({
			status: z.string(),
			publishedUrl: z.string().nullable().optional(),
		})
		.optional(),
});

type DeskPane = "chat" | "address" | "messages";

function previewSiteId(url: string): string | null {
	try {
		const path = url.startsWith("http")
			? new URL(url).pathname
			: (url.split("?")[0] ?? url);
		return path.match(PREVIEW_SITE_ID_RE)?.[1] ?? null;
	} catch {
		return null;
	}
}

/**
 * Address + Messages sit in the chat column once a hosted site is open —
 * the left pane of the split, not under the iframe or the Websites nav.
 */
export function WebsiteDeskStrip({
	previewUrl,
	host,
	onPublish,
	children,
}: {
	previewUrl: string | null;
	host: string;
	onPublish?: (siteId: string, siteName: string) => void;
	children: ReactNode;
}) {
	const siteId = useMemo(
		() => (previewUrl ? previewSiteId(previewUrl) : null),
		[previewUrl],
	);
	const [pane, setPane] = useState<DeskPane>("chat");
	const [published, setPublished] = useState(false);
	const [publishedUrl, setPublishedUrl] = useState<string | null>(null);
	const [tone, setTone] = useState<SiteTone>("ready");
	const [publishing, setPublishing] = useState(false);

	useEffect(() => {
		setPane("chat");
	}, [previewUrl]);

	useEffect(() => {
		if (!siteId) return;
		let cancelled = false;
		const load = async () => {
			try {
				const response = await fetch(`/api/sites/${siteId}/status`, {
					credentials: "include",
				});
				const json: unknown = await response.json();
				const parsed = hostedStatusSchema.safeParse(json);
				const site = parsed.success ? parsed.data.site : undefined;
				if (cancelled || !site) return;
				const live = Boolean(site.publishedUrl?.trim());
				setTone(siteTone(site.status));
				setPublished(site.status === "published" || live);
				setPublishedUrl(site.publishedUrl ?? null);
				setPublishing(site.status === "publishing" && !live);
			} catch {
				/* keep defaults */
			}
		};
		void load();
		return () => {
			cancelled = true;
		};
	}, [siteId]);

	useEffect(() => {
		if (!siteId || !publishing) return;
		const timer = window.setInterval(() => {
			void fetch(`/api/sites/${siteId}/status`, { credentials: "include" })
				.then(async (response) => {
					const json: unknown = await response.json();
					const parsed = hostedStatusSchema.safeParse(json);
					const site = parsed.success ? parsed.data.site : undefined;
					if (!site) return;
					const live = Boolean(site.publishedUrl?.trim());
					setTone(siteTone(site.status));
					setPublished(site.status === "published" || live);
					setPublishedUrl(site.publishedUrl ?? null);
					setPublishing(site.status === "publishing" && !live);
				})
				.catch(() => {
					/* keep */
				});
		}, 3000);
		return () => {
			window.clearInterval(timer);
		};
	}, [siteId, publishing]);

	if (!previewUrl || !siteId) return <>{children}</>;

	if (pane === "address" || pane === "messages") {
		return (
			<div className="mc-website-desk">
				<button
					type="button"
					className="mc-website-desk-back"
					onClick={() => setPane("chat")}
				>
					← Chat
				</button>
				{pane === "address" ? (
					<WebsiteAddressPanel
						host={host}
						published={published}
						publishedUrl={publishedUrl}
						tone={tone}
						publishing={publishing}
						onPublish={
							onPublish && siteId
								? () => onPublish(siteId, host)
								: undefined
						}
					/>
				) : (
					<WebsiteMessagesPanel />
				)}
			</div>
		);
	}

	return (
		<div className="mc-website-desk-wrap">
			<nav className="mc-website-desk-tiles" aria-label="On this website">
				<button
					type="button"
					className="sites-tile"
					onClick={() => setPane("address")}
				>
					<span className="sites-tile-icon" aria-hidden>
						<GlobeIcon className="size-4" />
					</span>
					<span className="min-w-0 flex-1 text-left">
						<span className="sites-hub-name block">Address</span>
						<span className="sites-hub-meta block">
							{publishedUrl
								? "Live on Mairko · your domain next"
								: `${host} · only you can see this`}
						</span>
					</span>
					<ChevronRightIcon
						className="size-4 shrink-0 text-mx-quaternary"
						aria-hidden
					/>
				</button>
				<button
					type="button"
					className="sites-tile"
					onClick={() => setPane("messages")}
				>
					<span className="sites-tile-icon" aria-hidden>
						<FileTextIcon className="size-4" />
					</span>
					<span className="min-w-0 flex-1 text-left">
						<span className="sites-hub-name block">Messages</span>
						<span className="sites-hub-meta block">
							0 so far · from the website forms
						</span>
					</span>
					<ChevronRightIcon
						className="size-4 shrink-0 text-mx-quaternary"
						aria-hidden
					/>
				</button>
			</nav>
			{children}
		</div>
	);
}
